import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SERVICE_OPTIONS = [
  { value: "warranty-repair", label: "Warranty System Repair" },
  { value: "detach-reattach", label: "System Detach & Re-Attach" },
  { value: "addition-expansion", label: "Addition & Expansion" },
  { value: "site-inspection", label: "Site Inspection Quality Control" },
  { value: "technical-training", label: "PV Installation & Commissioning Training" },
  { value: "operations-gap", label: "Operations Management Gap Filling" },
  { value: "other", label: "Other / General Inquiry" },
];

const initial = (service) => ({ name: "", email: "", phone: "", service_type: service || "other", message: "" });

export const ContactForm = ({ defaultService }) => {
  const [form, setForm] = useState(initial(defaultService));
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (form.message.trim().length < 10) {
      toast.error("Please tell us a little more (at least 10 characters).");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, { ...form, phone: form.phone || null });
      setSent(true);
      toast.success("Message sent. Our team will be in touch shortly.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div data-testid="contact-success" className="flex flex-col items-start rounded-2xl border border-border bg-background p-10">
        <CheckCircle2 className="h-10 w-10 text-primary" strokeWidth={1.25} />
        <h3 className="mt-6 font-display text-2xl font-light text-foreground">Thank you, {form.name.split(" ")[0]}.</h3>
        <p className="mt-3 text-base font-light text-muted-foreground">
          Your inquiry has been received. We typically respond within one business day.
        </p>
        <button
          data-testid="contact-send-another"
          onClick={() => { setForm(initial(defaultService)); setSent(false); }}
          className="mt-8 text-sm font-medium text-primary transition-colors hover:text-accent"
        >
          Send another message
        </button>
      </div>
    );
  }

  return (
    <form data-testid="contact-form" onSubmit={submit} className="rounded-2xl border border-border bg-background p-8 sm:p-10">
      <div className="grid gap-6 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="name">Full name</Label>
          <Input id="name" data-testid="contact-name-input" required minLength={2} value={form.name} onChange={set("name")} placeholder="Jane Smith" className="h-12 rounded-xl bg-secondary/40" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" data-testid="contact-email-input" required value={form.email} onChange={set("email")} placeholder="jane@example.com" className="h-12 rounded-xl bg-secondary/40" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="phone">Phone <span className="text-muted-foreground">(optional)</span></Label>
          <Input id="phone" type="tel" data-testid="contact-phone-input" value={form.phone} onChange={set("phone")} placeholder="(513) 555-0100" className="h-12 rounded-xl bg-secondary/40" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="service">Service needed</Label>
          <Select value={form.service_type} onValueChange={(v) => setForm({ ...form, service_type: v })}>
            <SelectTrigger id="service" data-testid="contact-service-select" className="h-12 rounded-xl bg-secondary/40">
              <SelectValue placeholder="Select a service" />
            </SelectTrigger>
            <SelectContent>
              {SERVICE_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value} data-testid={`contact-service-option-${o.value}`}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2 sm:col-span-2">
          <Label htmlFor="message">How can we help?</Label>
          <Textarea id="message" data-testid="contact-message-input" required rows={6} value={form.message} onChange={set("message")} placeholder="Tell us about your system, the issue you're seeing, or the support your business needs." className="rounded-xl bg-secondary/40" />
        </div>
      </div>
      <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-muted-foreground">Every inquiry is reviewed by our service team. No spam, no sales pressure.</p>
        <button
          type="submit"
          disabled={loading}
          data-testid="contact-submit-button"
          className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-medium text-white transition-[transform,box-shadow,opacity] duration-200 hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-60 disabled:hover:translate-y-0"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          {loading ? "Sending..." : "Send Message"}
        </button>
      </div>
    </form>
  );
};
